
import React, { useRef, useState } from "react";
import { LinkInBioProfile } from "@/types/linkinbio";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Upload, X, Loader2 } from "lucide-react";
import { SupabaseStorage } from "@/lib/supabase-storage";
import { supabase } from "@/integrations/supabase/client";
import { shadcnToast } from "@/hooks/use-toast";

interface AppearanceSectionProps {
  profile: LinkInBioProfile;
  onProfileUpdate: (profile: Partial<LinkInBioProfile>) => void;
}

export const AppearanceSection: React.FC<AppearanceSectionProps> = ({
  profile,
  onProfileUpdate,
}) => {
  const [isUploadingBackground, setIsUploadingBackground] = useState(false);
  const backgroundInputRef = useRef<HTMLInputElement>(null);
  
  const handleBackgroundImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setIsUploadingBackground(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // Delete old background image if exists
      if (profile.backgroundImage) {
        const oldPath = SupabaseStorage.getPathFromUrl(profile.backgroundImage);
        if (oldPath) {
          await SupabaseStorage.deleteImage(oldPath);
        }
      }
      
      // Generate new path and upload
      const imagePath = SupabaseStorage.generateProfileImagePath(user.id, 'background');
      const imageUrl = await SupabaseStorage.uploadImage(file, imagePath);
      
      if (imageUrl) {
        onProfileUpdate({ backgroundImage: imageUrl });
        shadcnToast({
          description: "Background image uploaded successfully!"
        });
      } else {
        throw new Error('Failed to upload image');
      }
    } catch (error: any) {
      console.error('Error uploading background image:', error);
      shadcnToast({
        description: error.message || "Failed to upload background image",
        variant: "destructive"
      });
    } finally {
      setIsUploadingBackground(false);
      // Reset the input
      if (backgroundInputRef.current) {
        backgroundInputRef.current.value = '';
      }
    }
  };
  
  const handleRemoveBackgroundImage = async () => {
    try {
      // Delete from storage if it's a Supabase URL
      if (profile.backgroundImage) {
        const imagePath = SupabaseStorage.getPathFromUrl(profile.backgroundImage);
        if (imagePath) {
          await SupabaseStorage.deleteImage(imagePath);
        }
      }
      
      onProfileUpdate({ backgroundImage: undefined });
      shadcnToast({
        description: "Background image removed successfully!"
      });
    } catch (error) {
      console.error('Error removing background image:', error);
      shadcnToast({
        description: "Failed to remove background image",
        variant: "destructive"
      });
    }
  };
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="layout">Layout</Label>
          <Select 
            value={profile.layout || "business"} 
            onValueChange={(value) => onProfileUpdate({ layout: value as LinkInBioProfile["layout"] })}
          >
            <SelectTrigger id="layout" className="w-full">
              <SelectValue placeholder="Select layout" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="business">Business</SelectItem>
              <SelectItem value="creative">Creative</SelectItem>
              <SelectItem value="minimal">Minimal</SelectItem>
              <SelectItem value="fancy">Fancy</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground mt-1">
            Choose the overall layout of your page
          </p>
        </div>

        <div>
          <Label htmlFor="theme">Theme</Label>
          <Select 
            value={profile.theme} 
            onValueChange={(value) => onProfileUpdate({ theme: value as LinkInBioProfile["theme"] })}
          >
            <SelectTrigger id="theme" className="w-full">
              <SelectValue placeholder="Select theme" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="minimal">Minimal</SelectItem>
              <SelectItem value="gradient">Gradient</SelectItem>
              <SelectItem value="dark">Dark</SelectItem>
              <SelectItem value="light">Light</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="font">Font</Label>
          <Select 
            value={profile.font} 
            onValueChange={(value) => onProfileUpdate({ font: value as LinkInBioProfile["font"] })}
          >
            <SelectTrigger id="font" className="w-full">
              <SelectValue placeholder="Select font" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default</SelectItem>
              <SelectItem value="serif">Serif</SelectItem>
              <SelectItem value="mono">Monospace</SelectItem>
              <SelectItem value="rounded">Rounded</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Button Style</Label>
          <RadioGroup 
            value={profile.buttonStyle}
            onValueChange={(value) => onProfileUpdate({ buttonStyle: value as "rounded" | "square" | "pill" })}
            className="flex gap-4 mt-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="rounded" id="rounded" />
              <Label htmlFor="rounded" className="cursor-pointer">Rounded</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="square" id="square" />
              <Label htmlFor="square" className="cursor-pointer">Square</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="pill" id="pill" />
              <Label htmlFor="pill" className="cursor-pointer">Pill</Label>
            </div>
          </RadioGroup>
        </div>
        
        <div>
          <Label>Show Booking Button</Label>
          <div className="flex items-center space-x-2 mt-2">
            <Switch 
              id="show-booking-button"
              checked={profile.showBookingButton !== false}
              onCheckedChange={(checked) => onProfileUpdate({ showBookingButton: checked })}
            />
            <Label htmlFor="show-booking-button" className="cursor-pointer">
              Display "Book Appointment" button on your LinkInBio page
            </Label>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            This will show a prominent booking button that links to your appointment booking page
          </p>
        </div>
      </div>
      
      {profile.theme === "custom" && (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div>
              <Label htmlFor="backgroundColor">Background Color</Label>
              <div className="flex gap-2 mt-1.5">
                <Input 
                  type="color" 
                  id="backgroundColor" 
                  value={profile.backgroundColor || "#ffffff"} 
                  onChange={(e) => onProfileUpdate({ backgroundColor: e.target.value })}
                  className="w-12 h-8 p-1"
                />
                <Input 
                  type="text" 
                  value={profile.backgroundColor || "#ffffff"} 
                  onChange={(e) => onProfileUpdate({ backgroundColor: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="buttonColor">Button Color</Label>
              <div className="flex gap-2 mt-1.5">
                <Input 
                  type="color" 
                  id="buttonColor" 
                  value={profile.buttonColor || "#000000"} 
                  onChange={(e) => onProfileUpdate({ buttonColor: e.target.value })}
                  className="w-12 h-8 p-1"
                />
                <Input 
                  type="text" 
                  value={profile.buttonColor || "#000000"} 
                  onChange={(e) => onProfileUpdate({ buttonColor: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="textColor">Text Color</Label>
              <div className="flex gap-2 mt-1.5">
                <Input 
                  type="color" 
                  id="textColor" 
                  value={profile.textColor || "#000000"} 
                  onChange={(e) => onProfileUpdate({ textColor: e.target.value })}
                  className="w-12 h-8 p-1"
                />
                <Input 
                  type="text" 
                  value={profile.textColor || "#000000"} 
                  onChange={(e) => onProfileUpdate({ textColor: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Background Image</Label>
              <div className="mt-2 space-y-3">
                {profile.backgroundImage && (
                  <div className="relative inline-block">
                    <img 
                      src={profile.backgroundImage} 
                      alt="Background preview" 
                      className="w-32 h-20 object-cover rounded border"
                    />
                    <button 
                      onClick={handleRemoveBackgroundImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      aria-label="Remove background image"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
                
                <input
                  type="file"
                  ref={backgroundInputRef}
                  onChange={handleBackgroundImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => backgroundInputRef.current?.click()}
                  disabled={isUploadingBackground}
                >
                  {isUploadingBackground ? (
                    <>
                      <Loader2 size={14} className="mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={14} className="mr-2" />
                      Upload Background
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-muted-foreground">
                  Upload a background image for your profile page
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
